import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { ADMIN_API } from "../utils/adminApi";

const STORAGE_KEY = "authUser";

const AuthContext = createContext(undefined);

function AuthProvider({ children }) {
  const hasWindow = typeof window !== "undefined";
  const [user, setUser] = useState(() => {
    if (!hasWindow) {
      return null;
    }
    try {
      const storedUser = window.localStorage.getItem(STORAGE_KEY);
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("Failed to parse stored auth user", error);
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const login = useCallback(async ({ phoneNumber, password }) => {
    setLoading(true);
    try {
      const base = ADMIN_API;
      const url = new URL("/api/login", base);

      // Credentials go in the body: a URL ends up in server logs and browser history
      const response = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ phoneNumber, password }),
      });

      if (!response.ok) {
        throw new Error("Unable to login. Please check your credentials.");
      }

      const data = await response.json();

      if (!data?.role) {
        throw new Error("Login response is missing role information.");
      }

      if (data.role !== "ROLE_ADMIN") {
        throw new Error("Access restricted to admin users.");
      }

      const authData = {
        role: data.role,
        phoneNumber,
        ...(data.access_token ? { token: data.access_token } : {}),
      };

      if (hasWindow) {
        try {
          window.localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
        } catch (error) {
          console.error("Failed to persist auth data", error);
        }
      }

      setUser(authData);
      return authData;
    } finally {
      setLoading(false);
    }
  }, [hasWindow]);

  const logout = useCallback(() => {
    setUser(null);
    if (hasWindow) {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error("Failed to remove auth data", error);
      }
    }
  }, [hasWindow]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      loading,
      login,
      logout,
    }),
    [user, loading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuth };
